// src/auth/AuthContext.jsx
// Lightweight auth context for the blog members area.
// - On mount, calls /api/auth/me to discover an existing session
// - login / signup / logout actions all use credentials: "include" cookies
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => ({ ok: false }),
  signup: async () => ({ ok: false }),
  changePassword: async () => ({ ok: false }),
  updateName: async () => ({ ok: false }),
  deleteAccount: async () => ({ ok: false }),
  logout: async () => {},
  refresh: async () => {}
});

async function postJSON(path, body) {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {})
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* noop */
  }
  return { res, data };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && data.ok && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async ({ email, password }) => {
    const { res, data } = await postJSON("/api/auth/login", { email, password });
    if (res.ok && data && data.ok) {
      setUser(data.user);
      return { ok: true, user: data.user };
    }
    return { ok: false, error: (data && data.error) || "Login failed." };
  }, []);

  const signup = useCallback(async ({ email, password, name }) => {
    const { res, data } = await postJSON("/api/auth/signup", { email, password, name });
    if (res.ok && data && data.ok) {
      setUser(data.user);
      return { ok: true, user: data.user };
    }
    return { ok: false, error: (data && data.error) || "Could not sign up." };
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const { res, data } = await postJSON("/api/auth/password", {
      current_password: currentPassword,
      new_password: newPassword
    });
    if (res.ok && data && data.ok) return { ok: true };
    return { ok: false, error: (data && (data.detail || data.error)) || "Could not change password." };
  }, []);

  const updateName = useCallback(async (name) => {
    const { res, data } = await postJSON("/api/auth/profile", { name });
    if (res.ok && data && data.ok) { setUser(data.user); return { ok: true }; }
    return { ok: false, error: (data && data.error) || "Could not save your name." };
  }, []);

  const deleteAccount = useCallback(async ({ password }) => {
    const { res, data } = await postJSON("/api/auth/delete", { password, confirm: "DELETE" });
    if (res.ok && data && data.ok) { setUser(null); return { ok: true }; }
    return { ok: false, error: (data && data.error) || "Could not delete the account." };
  }, []);

  const logout = useCallback(async () => {
    await postJSON("/api/auth/logout", {});
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, changePassword, updateName, deleteAccount, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
